import * as THREE from "three";
import { Context } from "../core/context";
import { DbLine, DbMesh, FMathUtils, generateUUID } from "@/CadEngine";
import {
  DrawOperationMovePointBeforeParams,
  DrawOperationMovePointParams,
  IModifyOperation,
  ModifyOperateState,
} from "./IModifyOperation";

import {
  CommonColor,
  InsertDatas,
  InsertFilePassMatrixAcceptParams,
  InsertFilePassMatrixReturnData,
} from "@/core/Parameters";

class InsertFilePassMatrixsModify implements IModifyOperation {
  private m_context: Context;
  private m_returnData: InsertFilePassMatrixReturnData = {
    extendId: [],
    insertDatas: [],
  };
  private m_curPoint: THREE.Vector3 = new THREE.Vector3();
  private m_points: THREE.Vector3[] = [];
  private m_scale: number = 1;
  private m_insertGroup!: THREE.Group;
  private m_insertDatas: InsertDatas = { lines: {}, meshs: {} };
  private m_acceptParams: InsertFilePassMatrixAcceptParams;
  private m_copyInsertDatas: InsertDatas[] = [];

  constructor(
    context: Context,
    acceptParams: InsertFilePassMatrixAcceptParams
  ) {
    this.m_context = context;
    this.m_scale = this.m_context.ViewManager.ScreenModelScale;
    this.m_context.ModelManager.isInsert = true;
    this.m_acceptParams = acceptParams;
    this.loadInsertModel();
  }

  OnMovePointBefore(params: DrawOperationMovePointBeforeParams): void {}
  OnMovePoint(point: DrawOperationMovePointParams): void {}
  OnDownPoint(point: DrawOperationMovePointParams): void {}
  onKeyDown(e: KeyboardEvent): void {}
  onKeyUp(e: KeyboardEvent): void {}

  OnZoom(camera: THREE.Camera): void {}

  Finish(operate: ModifyOperateState) {
    this.clear();
    this.m_context.ModelManager.isInsert = false;
    if (operate == ModifyOperateState.complete) {
      return this.m_returnData;
    } else if (operate == ModifyOperateState.cancel) {
      return [];
    }
  }
  OnWheel(e: WheelEvent): void {
    this.m_scale = this.m_context.ViewManager.ScreenModelScale;
  }

  onMouseMove(e?: PointerEvent): void {}

  onMouseDown(e: PointerEvent): void {}

  onMouseUp(e: PointerEvent): void {}

  private insertCallBack() {
    let insertData: (DbLine | DbMesh)[] = [];
    for (let i = 0; i < this.m_acceptParams?.matrixs.length; i++) {
      let curMatrix = this.m_acceptParams?.matrixs[i];
      let unifyExtendId = generateUUID();
      let unifyRootReferenceId = generateUUID();
      let copyInsertData = this.cloneInsertDatas(this.m_insertDatas);
      for (let key in copyInsertData.lines) {
        let lines = copyInsertData.lines[key];
        for (let i = 0; i < lines.lines.length; i++) {
          lines.lines[i].extend_id = unifyExtendId;
          lines.lines[i].root_reference_id = unifyRootReferenceId;
          let { points } = lines.lines[i];
          for (let j = 0; j < points.length; j += 3) {
            let point = new THREE.Vector3(
              points[j],
              points[j + 1],
              points[j + 2]
            );
            point.applyMatrix4(curMatrix);
            points[j] = point.x;
            points[j + 1] = point.y;
            points[j + 2] = point.z;
          }
          lines.lines[i].getBoundingBox();
          lines.lines[i].getBoundingSphere();
          this.m_context.ModelManager.SetReferences(lines.lines[i]);
          this.m_context.ModelManager.AddBlockInstance(
            lines.lines[i],
            curMatrix
          );
        }
        insertData.push(...lines.lines);
      }
      for (let key in copyInsertData.meshs) {
        let meshs = copyInsertData.meshs[key];
        for (let i = 0; i < meshs.meshs.length; i++) {
          meshs.meshs[i].extend_id = unifyExtendId;
          meshs.meshs[i].root_reference_id = unifyRootReferenceId;
          let { vertexs } = meshs.meshs[i];
          for (let j = 0; j < vertexs.length; j += 3) {
            let vertex = new THREE.Vector3(
              vertexs[j],
              vertexs[j + 1],
              vertexs[j + 2]
            );
            vertex.applyMatrix4(curMatrix);
            vertexs[j] = vertex.x;
            vertexs[j + 1] = vertex.y;
            vertexs[j + 2] = vertex.z;
          }
          meshs.meshs[i].getBoundingBox();
          meshs.meshs[i].getBoundingSphere();
          this.m_context.ModelManager.SetReferences(meshs.meshs[i]);
          this.m_context.ModelManager.AddBlockInstance(
            meshs.meshs[i],
            curMatrix
          );
        }
        insertData.push(...meshs.meshs);
      }
      this.m_returnData.extendId.push(unifyExtendId);
      this.m_copyInsertDatas.push(copyInsertData);
    }
    this.m_returnData.insertDatas.push(...insertData);
    this.updatedDataModel(insertData);
    this.m_context.ModifyManager.ModifyFinish(ModifyOperateState.complete);
  }

  private updatedDataModel(insertData: (DbLine | DbMesh)[]) {
    let { Lines, Meshs } = this.m_context.ModelManager;
    for (let i = 0; i < this.m_copyInsertDatas.length; i++) {
      let copyInsertData = this.m_copyInsertDatas[i];
      for (let key in copyInsertData.lines) {
        let lineData = copyInsertData.lines[key];
        if (!Lines[key]) {
          Lines[key] = lineData;
          this.m_context.SceneManager.ModelLayer.add(lineData.model);
        } else {
          Lines[key].lines.push(...lineData.lines);
        }
      }
      for (let key in copyInsertData.meshs) {
        let meshData = copyInsertData.meshs[key];
        if (!Meshs[key]) {
          Meshs[key] = meshData;
          this.m_context.SceneManager.ModelLayer.add(meshData.model);
        } else {
          Meshs[key].meshs.push(...meshData.meshs);
        }
      }
    }
    this.m_context.ModelManager.updateReferencesGeometry(insertData);
    let keyDatas = this.m_context.ToolsManager.filterRepeatKeyData(insertData);
    for (let i = 0; i < keyDatas.length; i++) {
      let key = keyDatas[i];
      let layerId = key.split("_")[0];
      let layer = this.m_context.ModelManager.LayerTable[layerId];
      if (Lines[key]) {
        this.m_context.ToolsManager.LinesToModel(
          Lines[key].lines,
          Lines[key].model
        );
        if (layer && !layer.Objects.includes(Lines[key].model)) {
          layer.Objects.push(Lines[key].model);
        }
      }
      if (Meshs[key]) {
        this.m_context.ToolsManager.MeshToModel(
          Meshs[key].meshs,
          Meshs[key].model
        );
        if (layer && !layer.Objects.includes(Meshs[key].model)) {
          layer.Objects.push(Meshs[key].model);
        }
      }
    }
  }
  private loadInsertModel() {
    this.m_context.ModelManager.addInsertGroup();
    this.m_context.LoadManager.LoadBlockP2d(
      this.m_acceptParams.file,
      true,
      () => {
        this.m_insertGroup = this.m_context.ModelManager.InsertGroup;
        this.m_insertDatas = this.m_context.ModelManager.InsertDatas;
        this.insertCallBack();
      }
    );
  }

  private cloneInsertDatas(sourceData: InsertDatas) {
    let copyDatas: InsertDatas = { lines: {}, meshs: {} };
    for (let key in sourceData.lines) {
      copyDatas.lines[key] = {
        canEdit: sourceData.lines[key].canEdit,
        model: sourceData.lines[key].model.clone(),
        lines: [],
      };
      copyDatas.lines[key].model.geometry = this.cloneInsertModelGeometry(
        sourceData.lines[key].model
      );
      for (let i = 0; i < sourceData.lines[key].lines.length; i++) {
        let line = sourceData.lines[key].lines[i];
        copyDatas.lines[key].lines.push(DbLine.clone(line));
      }
    }

    for (let key in this.m_insertDatas.meshs) {
      copyDatas.meshs[key] = {
        canEdit: sourceData.meshs[key].canEdit,
        model: this.m_insertDatas.meshs[key].model,
        meshs: [],
      };

      copyDatas.meshs[key].model.geometry = this.cloneInsertModelGeometry(
        sourceData.meshs[key].model
      );

      for (let i = 0; i < sourceData.meshs[key].meshs.length; i++) {
        let mesh = sourceData.meshs[key].meshs[i];
        copyDatas.meshs[key].meshs.push(DbMesh.clone(mesh));
      }
    }
    return copyDatas;
  }
  private cloneInsertModelGeometry(model: THREE.Object3D) {
    let sourceGeometryPositions = (model as THREE.Mesh).geometry.attributes
      .position.array;
    let sourceGeometryIndex = (model as THREE.Mesh).geometry.index;
    let copyGeometry = new THREE.BufferGeometry();
    copyGeometry.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array(sourceGeometryPositions), 3)
    );
    if (sourceGeometryIndex) {
      copyGeometry.setIndex(sourceGeometryIndex);
    }
    return copyGeometry;
  }

  private clear() {
    this.m_context.ToolsManager.GroupClear(this.m_insertGroup);
    this.m_context.ModelManager.InsertDatas = { lines: {}, meshs: {} };
  }
}

export { InsertFilePassMatrixsModify };
